import React, { useEffect, useState } from "react";
import {
  fetchActiveVendorAgreement,
  fetchVendorAgreementVersion,
  fetchVendorAgreementVersions,
  updateActiveVendorAgreement,
} from "../../services/vendorAgreementApi";
import { uploadImage } from "../../services/uploadApi";
import TableScroll from "../../components/TableScroll";
import "../../styles/admin-common.css";

const DEFAULT_COMPANY = "Nexonic Etch Private Limited";
const DEFAULT_ADDRESS = [
  "386, 2nd Floor Valarmathi Complex",
  "7th Street Cross Cut",
  "Gandhipuram, Coimbatore – 641012",
  "Tamil Nadu, India",
].join("\n");

const PLACEHOLDER_HELP = [
  "{{vendorName}}",
  "{{vendorEmail}}",
  "{{vendorPhone}}",
  "{{shopAddress}}",
  "{{signedAt}}",
  "{{agreementVersion}}",
  "{{companyLegalName}}",
].join(" · ");

const emptyForm = {
  title: "SpotDeal Vendor Partnership Agreement",
  bodyText: "",
  companyLegalName: DEFAULT_COMPANY,
  companyAddressLines: DEFAULT_ADDRESS,
  companySignatoryName: "",
  companySignatoryTitle: "Authorized Signatory",
  companySignatureImageUrl: "",
  changeSummary: "",
  isActive: true,
};

/** One admin place to edit SpotDeal vendor agreement content with version audit trail. */
const VendorAgreement = () => {
  const [form, setForm] = useState(emptyForm);
  const [meta, setMeta] = useState({ id: null, version: null });
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [viewingVersion, setViewingVersion] = useState(null);

  const loadVersions = async () => {
    try {
      setVersions(await fetchVendorAgreementVersions());
    } catch {
      setVersions([]);
    }
  };

  const load = async () => {
    setLoading(true);
    setError("");
    setViewingVersion(null);
    try {
      const data = await fetchActiveVendorAgreement();
      setForm({
        title: data.title || emptyForm.title,
        bodyText: data.bodyText || "",
        companyLegalName: data.companyLegalName || DEFAULT_COMPANY,
        companyAddressLines: data.companyAddressLines || DEFAULT_ADDRESS,
        companySignatoryName: data.companySignatoryName || "",
        companySignatoryTitle: data.companySignatoryTitle || "Authorized Signatory",
        companySignatureImageUrl: data.companySignatureImageUrl || "",
        changeSummary: "",
        isActive: data.isActive !== false,
      });
      setMeta({ id: data.id, version: data.version });
      await loadVersions();
    } catch (e) {
      setError(e.message || "Could not load agreement");
      setForm(emptyForm);
      setMeta({ id: null, version: null });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onChange = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onUploadSignature = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const uploaded = await uploadImage(file, "company-signatures");
      setForm((prev) => ({
        ...prev,
        companySignatureImageUrl: uploaded.url || uploaded,
      }));
      setOk("Company signature uploaded. Publish a new version to save it.");
    } catch (err) {
      setError(err.message || "Signature upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onViewVersion = async (id) => {
    setError("");
    try {
      const data = await fetchVendorAgreementVersion(id);
      setViewingVersion(data);
    } catch (err) {
      setError(err.message || "Could not load version");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.changeSummary.trim()) {
      setError("Change summary is required for the audit trail.");
      return;
    }
    setSaving(true);
    setError("");
    setOk("");
    try {
      const data = await updateActiveVendorAgreement({
        title: form.title.trim(),
        bodyText: form.bodyText.trim(),
        companyLegalName: form.companyLegalName.trim(),
        companyAddressLines: form.companyAddressLines.trim(),
        companySignatoryName: form.companySignatoryName.trim() || undefined,
        companySignatoryTitle: form.companySignatoryTitle.trim() || undefined,
        companySignatureImageUrl: form.companySignatureImageUrl.trim() || undefined,
        changeSummary: form.changeSummary.trim(),
        isActive: form.isActive,
      });
      setMeta({ id: data.id, version: data.version });
      setForm({
        title: data.title,
        bodyText: data.bodyText,
        companyLegalName: data.companyLegalName,
        companyAddressLines: data.companyAddressLines,
        companySignatoryName: data.companySignatoryName || "",
        companySignatoryTitle: data.companySignatoryTitle || "Authorized Signatory",
        companySignatureImageUrl: data.companySignatureImageUrl || "",
        changeSummary: "",
        isActive: data.isActive !== false,
      });
      setViewingVersion(null);
      await loadVersions();
      setOk(
        `Published version ${data.version}. Prior versions stay immutable in the audit trail. Vendor approvals stamp company signature onto the same PDF URL.`
      );
    } catch (err) {
      setError(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const formatWhen = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="admin-page">
      <h2>Vendor agreement</h2>
      <p className="subtitle">
        Each publish creates a <strong>new immutable version</strong> (audit trail). Old versions are
        never overwritten. Use placeholders so vendor details fill into the PDF body. On approval the
        company signature is stamped onto the <strong>same</strong> agreement PDF (no duplicate file).
      </p>

      {error && <p style={{ color: "#b91c1c", marginBottom: 12 }}>{error}</p>}
      {ok && <p style={{ color: "#15803d", marginBottom: 12 }}>{ok}</p>}

      {loading ? (
        <p>Loading…</p>
      ) : (
        <>
          <form className="admin-card admin-form" onSubmit={onSubmit} style={{ maxWidth: 820 }}>
            <p style={{ marginTop: 0, color: "#64748b" }}>
              {meta.version != null ? (
                <>
                  Active content id <strong>{meta.id}</strong> · version <strong>{meta.version}</strong>
                </>
              ) : (
                "No agreement seeded yet — publish the first version below."
              )}
            </p>

            <label className="field-label">
              <span>Title</span>
              <input value={form.title} onChange={onChange("title")} required maxLength={200} />
            </label>

            <label className="field-label">
              <span>Company legal name</span>
              <input
                value={form.companyLegalName}
                onChange={onChange("companyLegalName")}
                required
                maxLength={200}
              />
            </label>

            <label className="field-label">
              <span>Company address (one line per row)</span>
              <textarea
                rows={5}
                value={form.companyAddressLines}
                onChange={onChange("companyAddressLines")}
                required
              />
            </label>

            <label className="field-label">
              <span>Authorized signatory name</span>
              <input
                value={form.companySignatoryName}
                onChange={onChange("companySignatoryName")}
                maxLength={200}
                placeholder="e.g. Managing Director"
              />
            </label>

            <label className="field-label">
              <span>Authorized signatory title</span>
              <input
                value={form.companySignatoryTitle}
                onChange={onChange("companySignatoryTitle")}
                maxLength={200}
              />
            </label>

            <label className="field-label">
              <span>Company signature image</span>
              <input type="file" accept="image/*" onChange={onUploadSignature} disabled={uploading} />
              {uploading && <span className="muted-text"> Uploading…</span>}
              {form.companySignatureImageUrl && (
                <div style={{ marginTop: 8 }}>
                  <img
                    src={form.companySignatureImageUrl}
                    alt="Company signature"
                    style={{ maxHeight: 80, border: "1px solid #e2e8f0", background: "#fff", padding: 6 }}
                  />
                </div>
              )}
            </label>

            <label className="field-label">
              <span>Agreement body</span>
              <p className="muted-text" style={{ margin: "4px 0 8px", fontSize: 12 }}>
                Use <code>## Heading</code>, blank lines, <code>- bullet</code>. Placeholders filled at
                PDF generation: {PLACEHOLDER_HELP}
              </p>
              <textarea
                rows={22}
                value={form.bodyText}
                onChange={onChange("bodyText")}
                required
                style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13 }}
                placeholder={"## 1. Section\nVendor: {{vendorName}}\n\n- Bullet one"}
              />
            </label>

            <label className="field-label">
              <span>Change summary (audit trail — required)</span>
              <input
                value={form.changeSummary}
                onChange={onChange("changeSummary")}
                required
                maxLength={500}
                placeholder="e.g. Clarified fee change notice via email/app; added vendor placeholders"
              />
            </label>

            <label className="field-label" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={form.isActive} onChange={onChange("isActive")} />
              <span>Mark as active (vendors must use the active agreement)</span>
            </label>

            <div className="form-actions">
              <button type="submit" className="admin-btn" disabled={saving || uploading}>
                {saving ? "Publishing…" : "Publish new version"}
              </button>
              <button type="button" className="admin-btn secondary" onClick={load} disabled={saving}>
                Reload active
              </button>
            </div>
          </form>

          <div className="admin-card" style={{ marginTop: 24, maxWidth: 920 }}>
            <h3 style={{ marginTop: 0 }}>Version audit trail</h3>
            <p className="muted-text" style={{ marginTop: 0 }}>
              Every publish is a new row. Historical versions are read-only and kept for compliance.
            </p>
            {versions.length === 0 ? (
              <p>No versions yet.</p>
            ) : (
              <TableScroll>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Version</th>
                      <th>Status</th>
                      <th>Published</th>
                      <th>Admin</th>
                      <th>Change summary</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {versions.map((v) => (
                      <tr key={v.id}>
                        <td>v{v.version}</td>
                        <td>{v.isActive ? "Active" : "Superseded"}</td>
                        <td>{formatWhen(v.publishedAt)}</td>
                        <td>{v.publishedByAdminId ?? "—"}</td>
                        <td style={{ maxWidth: 280 }}>{v.changeSummary || "—"}</td>
                        <td>
                          <button
                            type="button"
                            className="admin-btn secondary"
                            style={{ padding: "4px 10px", fontSize: 12 }}
                            onClick={() => onViewVersion(v.id)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            )}
          </div>

          {viewingVersion && (
            <div className="admin-card" style={{ marginTop: 16, maxWidth: 920 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                <h3 style={{ margin: 0 }}>
                  Viewing v{viewingVersion.version}
                  {viewingVersion.isActive ? " (active)" : " (historical)"}
                </h3>
                <button type="button" className="admin-btn secondary" onClick={() => setViewingVersion(null)}>
                  Close
                </button>
              </div>
              <p className="muted-text">
                Published {formatWhen(viewingVersion.publishedAt)}
                {viewingVersion.changeSummary ? ` · ${viewingVersion.changeSummary}` : ""}
              </p>
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: 12,
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: 12,
                  maxHeight: 420,
                  overflow: "auto",
                }}
              >
                {viewingVersion.bodyText}
              </pre>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VendorAgreement;
